(() => {
  if (customElements.get('chastar-collection-products')) return;
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const quickMedia = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
  const hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
  const locks = new Set();
  const quickScripts = new Map();
  const quickStyles = new Map();
  const productSections = new Map();
  const loadQuickScript = source => {
    const url = new URL(source, location.href).href;
    if (quickScripts.has(url)) return quickScripts.get(url);
    if ([...document.scripts].some(script => script.src === url)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = setTimeout(() => {
        script.remove();
        reject(new Error('Product script timed out'));
      }, 15000);
      script.src = url;
      script.async = false;
      script.addEventListener('load', () => { clearTimeout(timeout); resolve(); }, { once: true });
      script.addEventListener('error', () => { clearTimeout(timeout); script.remove(); reject(new Error('Product script unavailable')); }, { once: true });
      document.head.append(script);
    }).catch(error => { quickScripts.delete(url); throw error; });
    quickScripts.set(url, promise);
    return promise;
  };
  const loadQuickStyle = source => {
    const url = new URL(source, location.href).href;
    if (quickStyles.has(url)) return quickStyles.get(url);
    if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(link => link.href === url)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      const timeout = setTimeout(() => {
        link.remove();
        reject(new Error('Product stylesheet timed out'));
      }, 15000);
      link.rel = 'stylesheet';
      link.href = url;
      link.addEventListener('load', () => { clearTimeout(timeout); resolve(); }, { once: true });
      link.addEventListener('error', () => { clearTimeout(timeout); link.remove(); reject(new Error('Product stylesheet unavailable')); }, { once: true });
      document.head.append(link);
    }).catch(error => { quickStyles.delete(url); throw error; });
    quickStyles.set(url, promise);
    return promise;
  };
  const fetchQuickProduct = async (url, template, signal) => {
    const key = `${template}|${url.searchParams.get('view') || ''}`;
    const section = productSections.get(key);
    const fetchURL = new URL(url);
    if (section) fetchURL.searchParams.set('section_id', section);
    const fetchRoot = async target => {
      const response = await fetch(target.href, { signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Product request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const main = doc.querySelector('#MainContent, main') || doc;
      const product = main.querySelector('product-info[id^="MainProduct"], product-info');
      if (!product?.dataset.section) throw new Error('Product information unavailable');
      return { product, root: product.closest('.shopify-section') || product, responseURL: response.url || target.href };
    };
    let result;
    try {
      result = await fetchRoot(fetchURL);
    } catch (error) {
      if (!section || signal.aborted || error.name === 'AbortError') throw error;
      productSections.delete(key);
      result = await fetchRoot(url);
    }
    productSections.set(key, result.product.dataset.section);
    return result;
  };
  let previousOverflow = '';
  const lock = dialog => {
    if (locks.size === 0) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    locks.add(dialog);
  };
  const unlock = dialog => {
    locks.delete(dialog);
    if (locks.size === 0) document.body.style.overflow = previousOverflow;
  };
  customElements.define('chastar-collection-products', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.classList.add('cc-products--enhanced');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.filterDialog = this.querySelector('[data-cc-filter-dialog]');
      this.quickDialog = this.querySelector('[data-cc-quick-dialog]');
      this.currentURL = location.href;
      quickMedia.addEventListener('change', () => {
        if (quickMedia.matches || !this.quickDialog?.open) return;
        this.quickDialog.ccAnimation?.cancel();
        this.quickDialog.close();
        unlock(this.quickDialog);
        this.clearQuickView();
      }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      const loadHoveredImage = event => {
        if (!hoverMedia.matches) return;
        const image = event.target.closest('[data-cc-card]')?.querySelector('.cc-card__image--secondary');
        if (image) this.loadSecondaryImage(image, 'auto');
      };
      this.addEventListener('pointerover', loadHoveredImage, options);
      this.addEventListener('focusin', loadHoveredImage, options);
      hoverMedia.addEventListener('change', () => this.observeSecondaryImages(), options);
      if (document.readyState === 'complete') this.observeSecondaryImages();
      else window.addEventListener('load', () => this.observeSecondaryImages(), { ...options, once: true });
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-cc-sort]')) return;
        const form = event.target.closest('form');
        this.navigate(this.formURL(form), { focus: '[data-cc-sort]' });
      }, options);
      [this.filterDialog, this.quickDialog].filter(Boolean).forEach(dialog => {
        dialog.addEventListener('cancel', event => {
          event.preventDefault();
          this.closeDialog(dialog);
        }, options);
        dialog.addEventListener('close', () => {
          if (dialog.open) return;
          if (locks.has(dialog)) unlock(dialog);
          if (dialog === this.quickDialog) this.clearQuickView();
        }, options);
      });
      if (!this.hasAttribute('data-cc-featured')) {
        window.addEventListener('popstate', () => {
          if (location.href !== this.currentURL) this.navigate(location.href, { history: false });
        }, options);
      }
      window.addEventListener('pageshow', event => {
        if (event.persisted) location.reload();
      }, options);
    }
    observeSecondaryImages() {
      this.secondaryObserver?.disconnect();
      if (!hoverMedia.matches || !this.isConnected) return;
      const images = this.querySelectorAll('img[data-cc-hover-src]');
      if (!images.length) return;
      if (!('IntersectionObserver' in window)) {
        images.forEach(image => this.loadSecondaryImage(image, 'low'));
        return;
      }
      this.secondaryObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.secondaryObserver.unobserve(entry.target);
          const load = () => {
            if (this.isConnected && hoverMedia.matches && entry.target.isConnected) this.loadSecondaryImage(entry.target, 'low');
          };
          if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 1200 });
          else setTimeout(load, 80);
        });
      }, { rootMargin: '240px' });
      images.forEach(image => this.secondaryObserver.observe(image));
    }
    loadSecondaryImage(image, priority) {
      if (!image.isConnected) return;
      if (priority === 'auto' || !image.hasAttribute('src')) image.fetchPriority = priority;
      const source = image.dataset.ccHoverSrc;
      if (!source) return;
      const srcset = image.dataset.ccHoverSrcset;
      delete image.dataset.ccHoverSrc;
      delete image.dataset.ccHoverSrcset;
      image.addEventListener('load', () => {
        if (image.naturalWidth > 0) image.closest('[data-cc-card]')?.setAttribute('data-secondary-ready', '');
      }, { once: true });
      image.loading = 'eager';
      if (srcset) image.srcset = srcset;
      image.src = source;
      if (image.complete && image.naturalWidth > 0) image.closest('[data-cc-card]')?.setAttribute('data-secondary-ready', '');
    }
    onClick(event) {
      const close = event.target.closest('[data-cc-close]');
      if (close) {
        this.closeDialog(close.closest('dialog'));
        return;
      }
      if (event.target.matches('dialog')) {
        const rect = event.target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog(event.target);
        return;
      }
      const filter = event.target.closest('[data-cc-filter-open]');
      if (filter) {
        this.openDialog(this.filterDialog, filter);
        return;
      }
      const quick = event.target.closest('[data-cc-quick]');
      if (quick) {
        if (quickMedia.matches && this.dataset.canView === 'true') this.openQuickView(quick);
        return;
      }
      const option = event.target.closest('[data-cc-editor-option]');
      if (option) {
        option.closest('fieldset').querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button === option)));
        this.updateEditorVariant();
        return;
      }
      const swatch = event.target.closest('[data-cc-swatch]');
      if (swatch && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.selectSwatch(swatch);
        return;
      }
      const link = event.target.closest('a[data-cc-navigate]');
      if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.navigate(link.href, { scroll: link.hasAttribute('data-cc-scroll'), closeFilter: link.hasAttribute('data-cc-reset'), focus: '[data-cc-filter-open]' });
      }
    }
    onSubmit(event) {
      const form = event.target.closest('[data-cc-filter-form], [data-cc-sort-form]');
      if (!form) return;
      event.preventDefault();
      this.navigate(this.formURL(form), { closeFilter: form.hasAttribute('data-cc-filter-form'), focus: '[data-cc-filter-open]' });
    }
    formURL(form) {
      const url = new URL(this.currentURL);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || key === 'sort_by') url.searchParams.delete(key);
      }
      for (const [key, value] of new FormData(form)) {
        if (String(value).trim() !== '') url.searchParams.append(key, value);
      }
      url.hash = '';
      return url;
    }
    async navigate(target, settings = {}) {
      const url = new URL(target, location.href);
      if (url.origin !== location.origin) return;
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.hash = '';
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-cc-status]');
      if (status) status.textContent = this.dataset.loadingLabel;
      const submit = this.querySelector('.cc-filter__apply');
      if (submit) submit.disabled = true;
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(fetchURL, { signal: request.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(String(response.status));
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.getElementById(this.id);
        if (!fresh?.querySelector('[data-cc-content]')) throw new Error('Collection section unavailable');
        const oldFields = this.querySelector('[data-cc-filter-fields]');
        const newFields = fresh.querySelector('[data-cc-filter-fields]');
        const state = new Map([...this.querySelectorAll('[data-facet-key]')].map(facet => [facet.dataset.facetKey, facet.open]));
        const scrollTop = oldFields?.scrollTop || 0;
        let replaced = false;
        const replace = () => {
          if (replaced) return;
          replaced = true;
          this.querySelector('[data-cc-content]').replaceWith(fresh.querySelector('[data-cc-content]'));
          this.observeSecondaryImages();
          this.dataset.canView = fresh.dataset.canView;
          if (oldFields && newFields) {
            oldFields.innerHTML = newFields.innerHTML;
            oldFields.querySelectorAll('[data-facet-key]').forEach(facet => {
              if (state.has(facet.dataset.facetKey)) facet.open = state.get(facet.dataset.facetKey);
            });
            oldFields.scrollTop = scrollTop;
          }
          const sort = this.querySelector('[data-cc-sort]')?.value || url.searchParams.get('sort_by') || '';
          const hiddenSort = this.querySelector('[data-cc-filter-sort]');
          if (hiddenSort) hiddenSort.value = sort;
          this.querySelectorAll('[data-cc-reset]').forEach(link => {
            const clear = new URL(this.dataset.collectionUrl, location.origin);
            if (sort) clear.searchParams.set('sort_by', sort);
            if (previewTheme) clear.searchParams.set('preview_theme_id', previewTheme);
            link.href = clear.href;
          });
        };
        if (document.startViewTransition && !reducedMotion() && !this.filterDialog?.open) {
          try {
            const transition = document.startViewTransition(replace);
            await transition.updateCallbackDone;
          } catch { replace(); }
        } else replace();
        if (settings.history !== false) history.pushState({ chastarCollection: true }, '', url);
        this.currentURL = url.href;
        if (settings.closeFilter) await this.closeDialog(this.filterDialog);
        if (settings.scroll) {
          const sticky = document.querySelector('.shopify-section-header-sticky, .chastar-header--sticky, sticky-header');
          const offset = sticky?.getBoundingClientRect().height || 24;
          const top = this.getBoundingClientRect().top + scrollY - offset - 12;
          scrollTo({ top: Math.max(0, top), behavior: reducedMotion() ? 'instant' : 'smooth' });
        }
        if (settings.focus) this.querySelector(settings.focus)?.focus({ preventScroll: true });
        if (status) status.textContent = this.querySelector('[data-cc-count]')?.textContent || '';
      } catch (error) {
        if (error.name !== 'AbortError') location.assign(url.href);
      } finally {
        if (this.request === request) {
          this.removeAttribute('aria-busy');
          if (submit) submit.disabled = false;
        }
      }
    }
    async openDialog(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog.removeAttribute('data-closing');
      dialog.ccOpener = opener;
      dialog.showModal();
      lock(dialog);
      const quick = dialog === this.quickDialog;
      const from = quick ? 'translateY(24px)' : 'translateX(-105%)';
      dialog.ccAnimation?.cancel();
      dialog.ccAnimation = dialog.animate([{ transform: from, opacity: .7 }, { transform: 'none', opacity: 1 }], { duration: reducedMotion() ? 0 : 360, easing: 'cubic-bezier(.22,1,.36,1)' });
      try { await dialog.ccAnimation.finished; } catch {}
    }
    async closeDialog(dialog) {
      if (!dialog?.open) return;
      if (dialog.ccClosing) return dialog.ccClosing;
      dialog.setAttribute('data-closing', '');
      dialog.ccAnimation?.cancel();
      const to = dialog === this.quickDialog ? 'translateY(24px)' : 'translateX(-105%)';
      dialog.ccClosing = (async () => {
        const animation = dialog.animate([{ transform: 'none', opacity: 1 }, { transform: to, opacity: .7 }], { duration: reducedMotion() ? 0 : 240, easing: 'cubic-bezier(.55,0,.8,.2)' });
        dialog.ccAnimation = animation;
        try { await animation.finished; } catch {}
        if (dialog.open) dialog.close();
        if (locks.has(dialog)) unlock(dialog);
        dialog.removeAttribute('data-closing');
        dialog.ccOpener?.focus({ preventScroll: true });
        dialog.ccClosing = null;
      })();
      return dialog.ccClosing;
    }
    async selectSwatch(swatch) {
      const card = swatch.closest('[data-cc-card]');
      if (!card) return;
      const image = card.querySelector('.cc-card__image--primary');
      const sequence = (card.ccSequence || 0) + 1;
      card.ccSequence = sequence;
      const unavailable = swatch.dataset.available === 'false';
      card.setAttribute('data-colour-selected', '');
      card.querySelectorAll('[data-cc-swatch]').forEach(link => link.setAttribute('aria-current', String(link === swatch)));
      card.querySelectorAll('[data-cc-product-link]').forEach(link => link.href = swatch.href);
      const overlay = card.querySelector('[data-cc-sold-out]');
      if (overlay) {
        overlay.hidden = !unavailable;
        overlay.setAttribute('aria-hidden', String(!unavailable));
      }
      const badge = card.querySelector('[data-cc-product-badge]');
      if (badge) badge.hidden = true;
      const quick = card.querySelector('[data-cc-quick]');
      if (quick) quick.dataset.productUrl = swatch.href;
      const price = card.querySelector('[data-cc-price]');
      if (price && swatch.dataset.price && this.dataset.canView === 'true') {
        const amount = document.createElement('span');
        amount.textContent = swatch.dataset.price;
        const tax = price.querySelector('small')?.cloneNode(true);
        price.replaceChildren(amount);
        if (tax) price.append(tax);
      }
      const announcer = card.querySelector('[data-cc-swatch-status]');
      if (announcer) announcer.textContent = swatch.dataset.label + (unavailable ? ` — ${this.dataset.soldOutLabel}` : '');
      if (!image || !swatch.dataset.src) return;
      const photo = new Image();
      photo.decoding = 'async';
      photo.sizes = image.sizes;
      if (swatch.dataset.srcset) photo.srcset = swatch.dataset.srcset;
      photo.src = swatch.dataset.src;
      try { await photo.decode(); } catch { return; }
      if (card.ccSequence !== sequence || !card.isConnected) return;
      image.srcset = swatch.dataset.srcset || '';
      image.src = swatch.dataset.src;
      image.alt = swatch.dataset.alt || '';
      image.style.opacity = '';
      card.ccImageAnimation?.cancel();
      const direction = Number(swatch.dataset.index) >= Number(card.ccColourIndex || 0) ? 1 : -1;
      card.ccColourIndex = swatch.dataset.index;
      card.ccImageAnimation = image.animate([{ opacity: .35, translate: `${direction * 14}px 0` }, { opacity: 1, translate: '0 0' }], { duration: reducedMotion() ? 0 : 260, easing: 'ease-out' });
    }
    updateEditorVariant(initialID) {
      const view = this.quickDialog?.querySelector('[data-cc-editor-view]');
      if (!view) return;
      const variants = JSON.parse(view.querySelector('[data-cc-editor-variants]').textContent);
      const fields = [...view.querySelectorAll('[data-cc-editor-position]')];
      if (initialID) {
        const initial = variants.find(variant => String(variant.id) === initialID);
        if (initial) fields.forEach((field, index) => field.querySelectorAll('[data-cc-editor-option]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.value === initial.options[index]));
        }));
      }
      const values = fields.map(field => field.querySelector('[aria-pressed="true"]')?.dataset.value);
      const variant = fields.length ? variants.find(candidate => candidate.options.every((value, index) => value === values[index])) : variants[0];
      fields.forEach((field, index) => {
        field.querySelectorAll('[data-cc-editor-option]').forEach(button => {
          const available = variants.some(candidate => candidate.available && candidate.options.every((value, position) => {
            return value === (position === index ? button.dataset.value : values[position]);
          }));
          button.classList.toggle('cc-editor-view__option--sold-out', !available);
        });
        const selected = field.querySelector('[data-cc-editor-selected]');
        if (selected) selected.textContent = values[index] || '';
      });
      const amount = view.querySelector('[data-cc-editor-price]');
      if (amount) amount.textContent = variant?.price || '—';
      const sku = view.querySelector('[data-cc-editor-sku]');
      if (sku) sku.textContent = variant?.sku || '—';
      const stock = view.querySelector('[data-cc-editor-stock]');
      if (stock) {
        stock.textContent = variant?.available ? view.dataset.availableLabel : this.dataset.soldOutLabel;
        stock.dataset.available = String(Boolean(variant?.available));
      }
      const image = view.querySelector('[data-cc-editor-image]');
      if (image && variant?.image) {
        image.src = variant.image;
        image.srcset = '';
      }
    }
    productURL(value) {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin) throw new Error('Product must be on the same storefront');
      const localeRoot = window.Shopify?.routes?.root;
      if (localeRoot && localeRoot !== '/' && url.pathname.startsWith('/products/')) {
        url.pathname = `${localeRoot.replace(/\/$/, '')}${url.pathname}`;
      }
      const theme = new URL(location.href).searchParams.get('preview_theme_id');
      if (theme) url.searchParams.set('preview_theme_id', theme);
      return url;
    }
    async openQuickView(opener) {
      const dialog = this.quickDialog;
      if (!dialog || !quickMedia.matches || this.dataset.canView !== 'true') return;
      this.clearQuickView();
      const body = dialog.querySelector('[data-cc-quick-body]');
      const status = dialog.querySelector('[data-cc-quick-status]');
      dialog.querySelector('[data-cc-quick-title]').textContent = opener.dataset.productTitle;
      const url = this.productURL(opener.dataset.productUrl);
      dialog.querySelector('[data-cc-quick-full]').href = url.href;
      this.openDialog(dialog, opener);
      if (this.dataset.editorPreview === 'true') {
        const template = opener.closest('[data-cc-card]').querySelector('template');
        if (template) body.append(template.content.cloneNode(true));
        this.updateEditorVariant(url.searchParams.get('variant'));
        status.textContent = '';
        return;
      }
      const skeleton = document.createElement('div');
      skeleton.className = 'cc-quick__skeleton';
      skeleton.setAttribute('aria-hidden', 'true');
      body.append(skeleton);
      body.setAttribute('aria-busy', 'true');
      status.textContent = this.dataset.loadingLabel;
      const request = new AbortController();
      this.quickRequest = request;
      this.quickTimeout = setTimeout(() => request.abort(), 25000);
      try {
        const { product, root, responseURL } = await fetchQuickProduct(url, opener.dataset.productTemplate || 'default', request.signal);
        const scripts = [...new Set([...root.querySelectorAll('script[src]')]
          .map(script => new URL(script.getAttribute('src'), responseURL))
          .filter(scriptURL => scriptURL.pathname.includes('/assets/'))
          .map(scriptURL => scriptURL.href))];
        const styles = [...new Set([...root.querySelectorAll('link[rel="stylesheet"]')]
          .map(link => new URL(link.getAttribute('href'), responseURL))
          .filter(styleURL => styleURL.pathname.includes('/assets/'))
          .map(styleURL => styleURL.href))];
        await Promise.all([...scripts.map(loadQuickScript), ...styles.map(loadQuickStyle)]);
        if (this.quickRequest !== request || !dialog.open) return;
        if (request.signal.aborted) throw new Error('Product request timed out');
        const originalSection = product.dataset.section;
        this.prepareQuickProduct(root, originalSection);
        root.querySelectorAll('script[src]').forEach(script => script.remove());
        root.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
          if (styles.includes(new URL(link.getAttribute('href'), responseURL).href)) link.remove();
        });
        const content = document.createElement('div');
        content.className = 'cc-quick__product';
        content.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback?.(node => this.prepareQuickProduct(node, originalSection));
          queueMicrotask(() => this.connectQuickCart(target));
        });
        content.append(document.importNode(root, true));
        body.replaceChildren(content);
        this.connectQuickCart(content);
        window.Shopify?.PaymentButton?.init();
        window.ProductModel?.loadShopifyXR?.();
        content.querySelector('product-component')?.dispatchViewEvent?.();
        status.textContent = '';
      } catch {
        if (this.quickRequest !== request || !dialog.open) return;
        body.replaceChildren();
        status.textContent = this.dataset.quickError;
      } finally {
        if (this.quickRequest === request) {
          clearTimeout(this.quickTimeout);
          body.removeAttribute('aria-busy');
        }
      }
    }
    prepareQuickProduct(root, fallbackSection) {
      const product = root.matches?.('product-info') ? root : root.querySelector?.('product-info');
      const original = product?.dataset.originalSection || product?.dataset.section || fallbackSection;
      if (!original) return;
      const namespace = `cc-quick-${this.dataset.sectionId}-${original}`;
      const prefix = value => value.split(namespace).map(part => part.replaceAll(original, namespace)).join(namespace);
      [root, ...root.querySelectorAll('*')].forEach(node => {
        for (const attribute of [...node.attributes]) {
          if (attribute.name !== 'data-original-section' && attribute.value.includes(original)) node.setAttribute(attribute.name, prefix(attribute.value));
        }
        if (node.tagName === 'STYLE') node.textContent = prefix(node.textContent);
        if (node.classList.contains('scroll-trigger')) node.classList.add('scroll-trigger--cancel');
        if (node.matches('chastar-dune-gallery')) node.dataset.sectionId = original;
        if (node.matches('product-info')) {
          node.dataset.originalSection = original;
          node.dataset.updateUrl = 'false';
        }
      });
    }
    connectQuickCart(root) {
      root.querySelectorAll('product-form').forEach(form => {
        const cart = form.cart;
        if (!cart?.renderContents || form.ccQuickCart) return;
        form.ccQuickCart = true;
        form.cart = new Proxy(cart, {
          get: (target, key) => {
            if (key === 'setActiveElement') return element => target.setActiveElement?.(this.quickDialog?.ccOpener || element);
            if (key === 'renderContents') return async data => {
              const status = this.quickDialog?.querySelector('[data-cc-quick-status]');
              if (status) status.textContent = this.dataset.addedLabel;
              if (this.quickDialog?.open) await this.closeDialog(this.quickDialog);
              target.renderContents(data);
            };
            const value = Reflect.get(target, key, target);
            return typeof value === 'function' ? value.bind(target) : value;
          },
        });
      });
    }
    clearQuickView() {
      clearTimeout(this.quickTimeout);
      this.quickRequest?.abort();
      this.quickRequest = null;
      const body = this.quickDialog?.querySelector('[data-cc-quick-body]');
      body?.querySelectorAll('product-info').forEach(product => product.abortController?.abort());
      body?.replaceChildren();
      body?.removeAttribute('aria-busy');
      const status = this.quickDialog?.querySelector('[data-cc-quick-status]');
      if (status) status.textContent = '';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.request?.abort();
      this.secondaryObserver?.disconnect();
      this.clearQuickView();
      [this.filterDialog, this.quickDialog].filter(Boolean).forEach(dialog => {
        dialog.ccAnimation?.cancel();
        if (dialog.open) dialog.close();
        if (locks.has(dialog)) unlock(dialog);
      });
    }
  });
})();
